import CategoryIcon from '@/components/cashflow/CategoryIcon';
import InstitutionLogo from '@/components/cashflow/InstitutionLogo';
import ApplyToCheckbox from '@/components/cashflow/rules/modal/ApplyToCheckbox';
import SubmitButton from '@/components/cashflow/rules/modal/SubmitButton';
import TagCircles from '@/components/cashflow/transactions/TagCircles';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { EyeOff, FlipBackward } from '@/components/common/icons';
import { LinkText } from '@/components/common/Typography';
import { toDollars } from '@/util/currency';
import { useQuery } from '@apollo/client';
import { Box, Text } from '@chakra-ui/react';
import {
    MerchantDocument,
    TransactionEntryCategoryFieldsFragment,
} from '@common/graphql/generated';
import {
    CashflowTransactionEntry,
    getMerchantName,
    useTags,
    useTransactionEntryCategories,
} from '@common/react/cashflow';
import {
    ChangeValues,
    getChangeValues,
    useRuleContext,
} from '@common/react/cashflow/rules';
import { dt } from '@common/util/dateTime';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';

const MODAL_HEIGHT_PX = 630;
const TABLE_HEIGHT_PX = 580;

const MatchingTransactionsTable = () => {
    const { matchingTransactions, enabledValidChanges, setView } =
        useRuleContext();

    const { transactionEntryCategoriesMapById } =
        useTransactionEntryCategories();

    const newMerchantId = enabledValidChanges.find(
        (c) => c.field === 'merchantId'
    )?.value;

    const { data: merchantQuery } = useQuery(MerchantDocument, {
        variables: { id: newMerchantId as string },
        skip: !newMerchantId,
    });

    const merchantName = merchantQuery?.merchant?.name;

    if (!matchingTransactions) return null;

    const changeValues = getChangeValues({
        enabledValidChanges,
        transactionEntryCategoriesMapById,
        merchantName,
    });

    // If the merchant name or category is being updated, the row height needs to be increased because we show both the old and new values
    const rowHeightPx =
        changeValues.merchantName || changeValues.category ? 96 : 64;

    return (
        <FlexCol h={MODAL_HEIGHT_PX} gap={4}>
            <AutoSizer disableHeight>
                {({ width }: { width: number }) => (
                    <List
                        height={TABLE_HEIGHT_PX}
                        itemCount={matchingTransactions.length}
                        itemSize={rowHeightPx}
                        width={width}
                        itemData={{
                            transactionEntries: matchingTransactions,
                            changeValues,
                        }}
                    >
                        {TableRow}
                    </List>
                )}
            </AutoSizer>
            <FlexRow align="center">
                <LinkText
                    whiteSpace="nowrap"
                    onClick={() => {
                        setView('form');
                    }}
                    flexGrow={1}
                >
                    <FlipBackward display="inline" mr={2} />
                    Back to rule
                </LinkText>
                <FlexRow align="center">
                    <ApplyToCheckbox mr={15} />
                    <SubmitButton />
                </FlexRow>
            </FlexRow>
        </FlexCol>
    );
};

const TableRow = ({
    index,
    style,
    data,
}: ListChildComponentProps<{
    transactionEntries: CashflowTransactionEntry[];
    changeValues: ChangeValues;
}>) => {
    const { transactionEntries, changeValues } = data;
    const transactionEntry = transactionEntries[index];
    const { tags } = useTags();
    const matchingTags = tags.filter((tag) =>
        changeValues.tagNames?.includes(tag.name)
    );

    return (
        <FlexRow
            style={style}
            py={5}
            px={2}
            borderBottom="1px solid"
            borderColor="border"
            fontSize="md"
            data-cy="matching-transaction-row"
        >
            <Box w="5%">
                <InstitutionLogo
                    financialEntity={transactionEntry.financialEntity}
                    showTooltip
                />
            </Box>
            <Box w="15%">{dt(transactionEntry.date).format('MMM D, YYYY')}</Box>
            <Box w="30%">
                <FlexRow justify="space-between" align="center">
                    <FlexCol gap={2}>
                        <Text
                            {...(changeValues.merchantName &&
                                STRIKETHROUGH_STYLES)}
                        >
                            {getMerchantName(transactionEntry)}
                        </Text>
                        {changeValues.merchantName && (
                            <Text>{changeValues.merchantName}</Text>
                        )}
                    </FlexCol>
                    {changeValues.isHidden && (
                        <EyeOff mr={4} color="gray.500" />
                    )}
                </FlexRow>
            </Box>
            <Box w="35%">
                <FlexCol gap={2} mt={-0.5}>
                    <CategoryRow
                        category={transactionEntry.category}
                        strikethrough={!!changeValues.category}
                    />
                    {changeValues.category && (
                        <CategoryRow category={changeValues.category} />
                    )}
                </FlexCol>
            </Box>
            <Box w="5%" textAlign="right">
                {matchingTags.length > 0 && (
                    <FlexRow mt={2} cursor="pointer">
                        <TagCircles tags={matchingTags} />
                    </FlexRow>
                )}
            </Box>
            <Box w="9%" textAlign="right">
                <Text whiteSpace="nowrap">
                    {toDollars(transactionEntry.amount, {
                        withCents: true,
                        alwaysSigned: true,
                    })}
                </Text>
            </Box>
        </FlexRow>
    );
};

const STRIKETHROUGH_STYLES = {
    textDecoration: 'line-through',
    textDecorationColor: 'red.300',
};

const CategoryRow = ({
    category,
    strikethrough,
}: {
    category: TransactionEntryCategoryFieldsFragment;
    strikethrough?: boolean;
}) => (
    <FlexRow
        align="center"
        gap={1}
        position="relative"
        {...(strikethrough && STRIKETHROUGH_STYLES)}
    >
        <Box ml={1}>
            <CategoryIcon category={category} />
        </Box>
        <Text
            ml={2}
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
        >
            {category.name}
        </Text>
    </FlexRow>
);

export default MatchingTransactionsTable;
